import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../services/api';
import SafeIcon from '../common/SafeIcon';
import { FiLock, FiMail, FiEye, FiEyeOff, FiLoader, FiAlertCircle, FiArrowRight, FiShield, FiUser } from 'react-icons/fi';

export default function AdminLogin() {
  const { adminUser, adminLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (adminUser) navigate('/admin');
  }, [adminUser, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email || !code) return setError("Please fill in all fields.");
    if (code.length !== 6) return setError("Code must be 6 digits.");

    setLoading(true);
    try {
      const response = await adminApi.verifyLogin(email, code, remember);
      if (response.success) {
        adminLogin({
          email: response.user.email,
          role: response.user.role,
          admin_session_token: response.admin_session_token,
          session_token: response.session_token,
          expires_at: response.expires_at
        }, remember);
      } else {
        setError(response.message || "Invalid credentials.");
      }
    } catch (err) {
      setError("Connection failure. Check Gateway URL.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-900 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl p-10 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-brand-blue/5 rounded-full -mr-16 -mt-16"></div>
        
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-navy-900 rounded-2xl flex items-center justify-center shadow-xl mx-auto mb-6 transform -rotate-6">
            <SafeIcon icon={FiShield} className="text-brand-cyan text-3xl" />
          </div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tighter uppercase">BPAS Admin Console</h1>
          <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mt-2 leading-relaxed">
            BPA Solutions internal access only
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-100 rounded-2xl flex flex-col gap-2">
              <div className="flex items-start gap-3 text-red-600 text-xs font-bold leading-relaxed">
                <SafeIcon icon={FiAlertCircle} className="mt-0.5 flex-shrink-0" />
                <p>{error}</p>
              </div>
              <p className="text-[10px] text-red-400 font-medium leading-relaxed italic ml-8">
                Admin access uses BPAS_ALLOWED_ADMIN_EMAILS and BPAS_APP_LOGIN_CODE from Google Apps Script Script Properties.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 ml-1">Admin Email</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300">
                <SafeIcon icon={FiMail} />
              </div>
              <input 
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@bpasolutions.au"
                className="w-full pl-11 pr-4 py-4 bg-slate-50 border-2 border-transparent focus:border-brand-blue/10 focus:ring-4 focus:ring-brand-blue/5 rounded-2xl text-sm font-bold outline-none transition-all"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 ml-1">Access Code</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300">
                <SafeIcon icon={FiLock} />
              </div>
              <input 
                type={showCode ? "text" : "password"} value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="••••••"
                className="w-full pl-11 pr-12 py-4 bg-slate-50 border-2 border-transparent focus:border-brand-blue/10 focus:ring-4 focus:ring-brand-blue/5 rounded-2xl text-lg font-mono font-black tracking-[0.5em] outline-none transition-all"
              />
              <button type="button" onClick={() => setShowCode(!showCode)} className="absolute inset-y-0 right-0 pr-4 text-slate-400">
                <SafeIcon icon={showCode ? FiEyeOff : FiEye} />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3">
             <input type="checkbox" id="rem" checked={remember} onChange={() => setRemember(!remember)} className="w-4 h-4 rounded border-slate-300 text-brand-blue" />
             <label htmlFor="rem" className="text-xs font-bold text-slate-500 cursor-pointer">Remember this console</label>
          </div>

          <button 
            type="submit" disabled={loading}
            className="w-full py-5 bg-navy-900 text-white rounded-2xl font-black uppercase tracking-widest text-xs flex items-center justify-center gap-3 hover:bg-navy-800 transition-all shadow-xl shadow-navy-900/20 active:scale-95 disabled:opacity-50"
          >
            {loading ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <><span>Authorize Access</span> <SafeIcon icon={FiArrowRight} /></>}
          </button>
        </form>

        <div className="mt-8 pt-8 border-t border-slate-100 flex justify-center">
          <Link 
            to="/login" 
            className="group flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-brand-blue transition-colors"
          >
            <SafeIcon icon={FiUser} className="text-xs" />
            <span><span className="underline underline-offset-4 group-hover:no-underline">Client portal login</span></span>
          </Link>
        </div>
      </div>
    </div>
  );
}